import type { User } from "@assetwatch/auth";
import { z } from "zod";
import { postMlApi } from "../../api/mlApi";
import config from "../../config";
import {
  assertCanAccessMlFrontend,
  type MlFrontendAccessFlags,
} from "../../lib/access";

/**
 * The MP Events page's own ML API endpoint — events from eight source tables in
 * the data lake, unioned by one Athena query. Same convention as `mp_graphs`:
 * one path per page, one `meth` per operation.
 */
const MP_EVENTS_PATH = `${config.mlApi.frontendPath}/mp_events`;

const eventTypesResponseSchema = z.object({
  event_types: z.array(z.string()),
});

/**
 * The event-type vocabulary that drives the filter. A server-side constant that
 * reads no data, so it's free and safe to call on page load.
 *
 * Needed as its own call because the filter has to be populated *before* the
 * events query runs — deriving the list from returned events would only ever let
 * you filter to a type already on screen.
 */
export async function fetchMpEventTypes({
  user,
  flags,
}: {
  user: User | null;
  flags: MlFrontendAccessFlags;
}): Promise<string[]> {
  assertCanAccessMlFrontend({ flags, user });

  const payload = await postMlApi(MP_EVENTS_PATH, {
    meth: "get_mp_event_types",
  });

  return eventTypesResponseSchema.parse(payload).event_types;
}

/**
 * `metadata` is a real object or array, never a stringified one — but a string is
 * parsed rather than dropped, because the same rows reach us as CSV elsewhere and
 * an escaping bug shouldn't blank the column.
 */
const eventMetadataSchema = z
  .preprocess(
    (value) => {
      if (typeof value !== "string") {
        return value;
      }
      const trimmed = value.trim();
      if (trimmed === "" || trimmed === "null") {
        return null;
      }
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    },
    z
      .union([z.record(z.unknown()), z.array(z.unknown())])
      .nullish()
      .transform((value) => value ?? null),
  )
  .catch(null);

/**
 * Epoch seconds, which is what the endpoint specifies — but tolerant of a
 * numeric string and of a raw DB `DATETIME` ("2026-07-27 03:38:09"), since eight
 * different source tables feed this and only one of them has to be inconsistent
 * to throw. A value with no zone is read as UTC, matching the DateCreated
 * convention. Anything unrecognized is left alone so the row fails its own parse
 * rather than being silently plotted at the epoch.
 */
const epochSeconds = z.preprocess((value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (/^-?\d+$/.test(trimmed)) {
    return Number(trimmed);
  }

  const normalized = trimmed.replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized);
  const parsed = Date.parse(hasZone ? normalized : `${normalized}Z`);

  return Number.isNaN(parsed) ? value : Math.floor(parsed / 1000);
}, z.number());

/**
 * Which `mp_graphs` charts this event points at, and which series to single out
 * on each. Named `graph_focus` rather than `graphs` because the MP Graphs
 * response already has a top-level `graphs` of a different shape.
 *
 * `key` is a catalog key that goes straight into `get_vibration_graphs`.
 * `highlight` holds `series[].label` strings ("X", "Y", "Z", "Temperature"), so
 * they match without a lookup table — and can be null, meaning "open this graph,
 * single nothing out".
 *
 * Only `rca_cf` events get a non-null `graph_focus`; everything else says
 * something happened without saying which metric, so the default chart set is the
 * path that runs most often. An enhancement, not a contract.
 */
const graphFocusEntrySchema = z.object({
  key: z.string(),
  highlight: z.array(z.string()).nullish().catch(null),
});

export type MpGraphFocusEntry = z.infer<typeof graphFocusEntrySchema>;

/** A span in epoch seconds, the same units as the event's `timestamp`. */
const graphFocusSpanSchema = z.object({
  start: z.coerce.number(),
  end: z.coerce.number(),
});

export type MpGraphFocusSpan = z.infer<typeof graphFocusSpanSchema>;

/**
 * Where to look and what to look at. The three parts are independent.
 *
 * `viewport` is the x-axis range to render — it also determines what to fetch,
 * since its `end` is the server's clock at request time rather than the event's
 * timestamp, so the chart runs to the present instead of stopping at a stale
 * event. `highlight_window` is the span inside it that the model actually
 * evaluated (its `end` is the event timestamp); it can extend past the viewport's
 * leading edge, so it's clamped when rendered. `graphs` narrows which charts open.
 *
 * Two different "use the defaults" states: a null `graph_focus` means the default
 * charts with no range override and no band, while a null `graphs` inside a
 * present `graph_focus` means the default charts *with* the range and the band.
 */
const graphFocusSchema = z.object({
  graphs: z.array(graphFocusEntrySchema).nullish().catch(null),
  highlight_window: graphFocusSpanSchema.nullish().catch(null),
  viewport: graphFocusSpanSchema.nullish().catch(null),
});

/**
 * One event.
 *
 * `sensor` is a sensor serial from the lake's metadata table, not a
 * monitoring-point name — those live in MySQL, which this endpoint doesn't reach.
 * It's null when the monitoring point has no metadata row.
 */
const mpEventSchema = z.object({
  /**
   * The same canonical GUID `mp_graphs` takes, so the "View graphs"
   * drill-through needs no translation.
   */
  external_monitoringpoint_id: z.string(),
  sensor: z.string().nullish().catch(null),
  /** A slug from `get_mp_event_types`; see `eventTypeLabel` for display. */
  event_type: z.string(),
  timestamp: epochSeconds,
  metadata: eventMetadataSchema,
  // Tolerated as absent so a row without it — or with a shape we don't
  // recognize — still renders and still drills through to the default charts.
  graph_focus: graphFocusSchema.nullish().catch(null),
});

export type MpEvent = z.infer<typeof mpEventSchema>;

/** Same shape `mp_graphs` reports, minus the source-batching query count. */
const eventsCostSchema = z.object({
  usd: z.number().nullish().catch(null),
  mb_scanned: z.number().nullish().catch(null),
  rows: z.number().nullish().catch(null),
  seconds: z.number().nullish().catch(null),
});

export type MpEventsCost = z.infer<typeof eventsCostSchema>;

const mpEventsResponseSchema = z.object({
  /**
   * Newest first.
   *
   * Rows are validated one at a time: eight independent pipelines write these,
   * and a single unexpected row must not blank a page of five thousand good
   * ones. Rejects are counted so the page can say it dropped them rather than
   * quietly under-reporting.
   */
  events: z.array(z.unknown()).transform((rows) => {
    const parsed: MpEvent[] = [];
    let dropped = 0;

    for (const row of rows) {
      const result = mpEventSchema.safeParse(row);
      if (result.success) {
        parsed.push(result.data);
      } else {
        dropped += 1;
      }
    }

    return { parsed, dropped };
  }),
  // `total` is the length of `events`, so it's parsed and dropped — the page
  // counts and pages the array it actually renders.
  total: z.coerce.number().nullish().catch(null),
  events_truncated: z.boolean().nullish().catch(null),
  cost: eventsCostSchema.nullish().catch(null),
});

export type MpEventsResult = {
  events: MpEvent[];
  /**
   * True when the 5,000-row cap was hit. There's no server-side pagination, so
   * this is the only signal that older events exist beyond what's shown.
   */
  truncated: boolean;
  /** Rows the response carried that couldn't be read. Normally 0. */
  dropped: number;
  cost: MpEventsCost | null;
};

export type FetchMpEventsInput = {
  user: User | null;
  flags: MlFrontendAccessFlags;
  /** Integer 1–365. The endpoint rejects 0; see `clampDaysBack`. */
  daysBack: number;
  /** A slug from `get_mp_event_types`. Omit for all types. */
  eventType?: string;
  /** Exact monitoring point id. Omit for all monitoring points. */
  monitoringPointId?: string;
  /** Substring of a monitoring point id or sensor serial. */
  search?: string;
};

export async function fetchMpEvents({
  user,
  flags,
  daysBack,
  eventType,
  monitoringPointId,
  search,
}: FetchMpEventsInput): Promise<MpEventsResult> {
  // Defense-in-depth: the route is flag- and role-gated upstream, and the
  // endpoint enforces IAM, but the check runs before the request is signed.
  assertCanAccessMlFrontend({ flags, user });

  // Absent filters are omitted rather than sent as null — the endpoint treats
  // both the same, and a body with only the applied filters is easier to read in
  // a network trace.
  const payload = await postMlApi(MP_EVENTS_PATH, {
    meth: "get_mp_events",
    days_back: daysBack,
    ...(eventType ? { event_type: eventType } : {}),
    ...(monitoringPointId
      ? { external_monitoringpoint_id: monitoringPointId }
      : {}),
    ...(search ? { search } : {}),
  });

  const parsed = mpEventsResponseSchema.parse(payload);

  return {
    events: parsed.events.parsed,
    truncated: parsed.events_truncated === true,
    dropped: parsed.events.dropped,
    cost: parsed.cost ?? null,
  };
}
